import { useCallback, useState, type ReactElement } from 'react';
import { AdminImageCropModal } from '@/components/admin/AdminImageCropModal';
import { readFileAsDataUrl, type AdminCropPresetId } from '@/lib/adminImageCrop';

export type AdminImageCropOpenOptions = {
  file: File;
  defaultPreset?: AdminCropPresetId;
  allowedPresets?: AdminCropPresetId[];
  title?: string;
  onConfirm: (file: File) => void | Promise<void>;
  onConfirmRaw?: (file: File) => void | Promise<void>;
};

export type UseAdminImageCropReturn = {
  cropModal: ReactElement;
  openFromFile: (opts: AdminImageCropOpenOptions) => Promise<void>;
  cropOpen: boolean;
};

// Name avoids TS 5.9 parsing `useAdminImageCrop` as React `use<AdminImageCrop>()`.
export function bindAdminImageCrop(): UseAdminImageCropReturn {
  const [source, setSource] = useState<string | null>(null);
  const [fileName, setFileName] = useState('image.jpg');
  const [defaultPreset, setDefaultPreset] = useState<AdminCropPresetId>('catalog_wide');
  const [allowedPresets, setAllowedPresets] = useState<AdminCropPresetId[] | undefined>();
  const [title, setTitle] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [handlers, setHandlers] = useState<{
    onConfirm: (file: File) => void | Promise<void>;
    onConfirmRaw?: (file: File) => void | Promise<void>;
  } | null>(null);

  const close = useCallback(() => {
    setSource(null);
    setHandlers(null);
    setSaving(false);
  }, []);

  const openFromFile = useCallback(async (opts: AdminImageCropOpenOptions) => {
    const dataUrl = await readFileAsDataUrl(opts.file);
    setFileName(opts.file.name || 'image.jpg');
    setDefaultPreset(opts.defaultPreset ?? 'catalog_wide');
    setAllowedPresets(opts.allowedPresets);
    setTitle(opts.title);
    setHandlers({
      onConfirm: opts.onConfirm,
      onConfirmRaw: opts.onConfirmRaw,
    });
    setSource(dataUrl);
  }, []);

  const cropModal: ReactElement = (
    <AdminImageCropModal
      open={Boolean(source)}
      source={source}
      fileName={fileName}
      title={title}
      defaultPreset={defaultPreset}
      allowedPresets={allowedPresets}
      saving={saving}
      onCancel={close}
      onConfirmRaw={handlers?.onConfirmRaw}
      onConfirm={async (file, _preset) => {
        if (!handlers) return;
        setSaving(true);
        try {
          await handlers.onConfirm(file);
          close();
        } finally {
          setSaving(false);
        }
      }}
    />
  );

  return { cropModal, openFromFile, cropOpen: Boolean(source) };
}
